/**
 * Login page — `/login`.
 *
 * Uses react-hook-form + zod resolver so the same validation that the API
 * runs on the server is applied client-side BEFORE the network round-trip.
 * The Zod schema is the shared `loginSchema` from `@ottopilot/shared` —
 * we never duplicate the rule on the client.
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import type { LoginInput } from "@ottopilot/shared";
import { loginSchema } from "@ottopilot/shared";
import { useLogin, useMe } from "../hooks/useAuth";
import { useToast } from "../components/ToastProvider";
import { makeZodResolver } from "../lib/zodResolver";

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const me = useMe();
  const login = useLogin();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: makeZodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  // If a session already exists, bounce straight to /sites — the operator
  // landed here by accident.
  useEffect(() => {
    if (me.data?.user) {
      void navigate({ to: "/sites" });
    }
  }, [me.data, navigate]);

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: () => {
        toast.showSuccess("Signed in.");
        void navigate({ to: "/sites" });
      },
      onError: (err) => {
        toast.showError(err.message || "Sign-in failed.");
      },
    });
  });

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper sx={{ p: { xs: 3, md: 4 }, border: 1, borderColor: "divider" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Welcome back. Use your operator account.
            </Typography>
          </Box>

          {login.isError ? (
            <Alert severity="error" data-testid="login-error-alert">
              {login.error.message}
            </Alert>
          ) : null}

          <Box
            component="form"
            onSubmit={(e) => {
              void onSubmit(e);
            }}
            noValidate
          >
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                inputProps={{ "data-testid": "login-email-input" }}
                {...register("email")}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                inputProps={{ "data-testid": "login-password-input" }}
                {...register("password")}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || login.isPending}
                data-testid="login-submit-button"
              >
                {login.isPending ? "Signing in..." : "Sign in"}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Need an account?{" "}
            <MuiLink component={Link} to="/signup" data-testid="login-signup-link">
              Sign up
            </MuiLink>
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
